import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAuthToken } from "../utils/cookieUtils";
import { getUserRole } from "../utils/tokenUtils";
import { getCurrentLocation } from "../utils/locationUtils";
import {
  addRecentBreakdown,
  removeRecentBreakdown,
} from "../utils/recentBreakdownsUtils";
import BreakdownCard from "../components/BreakdownCard";
import TyrePositionPicker from "../components/categories/TyrePositionPicker";
import { formatDate } from "../utils/dateUtils";
import { showCallOptionsModal } from "../utils/callUtils";
import PullToRefresh from "../components/PullToRefresh";
import {
  Card,
  Typography,
  Descriptions,
  Button,
  Divider,
  Spin,
  Space,
  Badge,
  Result,
  Tabs,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  notification,
  Upload,
  Image,
  Select,
  Collapse,
  Empty,
  List,
} from "antd";
import {
  CarOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  ClockCircleOutlined,
  SendOutlined,
  FileImageOutlined,
  UploadOutlined,
  InfoCircleOutlined,
  MessageOutlined,
  PictureOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  EditOutlined,
  CheckCircleOutlined,
  UserOutlined,
  CopyOutlined,
  ShareAltOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import authService from "../services/authService";
import breakdownCategoryService from "../services/breakdownCategoryService";
import {
  BREAKDOWN_STATUS_COLORS,
  API_CONFIG,
} from "../utils/constants";
import { loadMechanicStatuses } from "../services/mechanicStatusService";
import { loadBreakdownStatuses } from "../services/breakdownStatusService";
import {
  getSupplierMechanicList,
  assignMechanicAndSupplier,
  getAssignedMechanics,
  updateOldUserLocation,
} from "../utils/apiService";
import imageCacheManager, {
  getCachedImages,
  cacheImages,
  isCacheValid,
  compareImageIds,
} from "../utils/imageCacheUtils";
import "../assets/styles/breakdownDetails.css";
import "../assets/styles/categoryFaults.css";
import RestrictionModal from "../components/RestrictionModal";
import { useLocation } from "react-router-dom";

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;
const shareUrlBase = process.env.VITE_SHARE_URL_BASE;

const buildCategorySearchLabel = (item) =>
  [item.categoryName, item.subCategoryName, item.componentName].filter(Boolean).join(" | ");

const buildCategorySearchValue = (item) =>
  [item.categoryId, item.subCategoryId, item.componentId].filter(Boolean).join("|");

const upsertById = (items, item) => {
  if (!item?.id) return items;
  const exists = items.some((existing) => String(existing.id) === String(item.id));
  return exists
    ? items.map((existing) => String(existing.id) === String(item.id) ? { ...existing, ...item } : existing)
    : [...items, item];
};

const EXT_TO_MIME = {
  heic: "image/heic",
  heif: "image/heif",
}

const BreakdownDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userRole = getUserRole();
  const [breakdown, setBreakdown] = useState(null);
  const [notes, setNotes] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [error, setError] = useState(null);
  const [form] = Form.useForm();
  const messagesEndRef = useRef(null);
  const [breakdownStatuses, setBreakdownStatuses] = useState([]);
  const [allBreakdownStatuses, setAllBreakdownStatuses] = useState([]);
  const [noteSending, setNoteSending] = useState(false);
  // Get the current user from user data
  const [currentUser, setCurrentUser] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [mechanicDetails, setMechanicDetails] = useState(null);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  // Supplier mechanic assignment state
  const [mechanicOptions, setMechanicOptions] = useState([]);
  const [mechanicOptionsLoading, setMechanicOptionsLoading] = useState(false);
  const [selectedMechanicId, setSelectedMechanicId] = useState(null);
  const [assignConfirmVisible, setAssignConfirmVisible] = useState(false);
  const [assigningMechanic, setAssigningMechanic] = useState(false);
  const [assignedMechanics, setAssignedMechanics] = useState([]);
  const [assignedMechanicsLoading, setAssignedMechanicsLoading] =
    useState(false);

  // State for Complete Breakdown Form
  const [completeBreakdownModalVisible, setCompleteBreakdownModalVisible] =
    useState(false);
  const [confirmCompleteModalVisible, setConfirmCompleteModalVisible] =
    useState(false);
  const [breakdownCategories, setBreakdownCategories] = useState([]);
  const [breakdownSubcategories, setBreakdownSubcategories] = useState([]);
  const [breakdownComponents, setBreakdownComponents] = useState([]);
  const [breakdownCauses, setBreakdownCauses] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedCause, setSelectedCause] = useState(null);
  const [selectedTyres, setSelectedTyres] = useState([]);
  const [tyrePickerVisible, setTyrePickerVisible] = useState(false);
  const [completeCategorySearchTerm, setCompleteCategorySearchTerm] = useState("");
  const [completeCategorySearchValue, setCompleteCategorySearchValue] = useState(undefined);
  const [completeCategorySearchResults, setCompleteCategorySearchResults] = useState([]);
  const [completeCategorySearching, setCompleteCategorySearching] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [loadingComponents, setLoadingComponents] = useState(false);
  const [loadingCauses, setLoadingCauses] = useState(false);
  const [formData, setFormData] = useState({});
  const completeBreakdownForm = Form.useForm()[0];
  const [confirmModalType, setConfirmModalType] = useState(null); // 'accept' or 'decline'
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [vehicleHistory, setVehicleHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [confirmModalLoading, setConfirmModalLoading] = useState(false);

  const [etaModalVisible, setEtaModalVisible] = useState(false);
  const [etaHours, setEtaHours] = useState(1);
  const [etaMinutes, setEtaMinutes] = useState(0);

  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [statusReason, setStatusReason] = useState("");
  const [isShare, setIsShare] = useState(false);
  const [restriction, setRestriction] = useState(false);
  const isInIframe = window.self !== window.top;
  const { pathname } = useLocation();

  // Video modal state
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [currentVideoSrc, setCurrentVideoSrc] = useState('');
  const [currentVideoTitle, setCurrentVideoTitle] = useState('');

  const [media, setMedia] = useState([]); // instead of images

  const breakdownTypeMap = {
    0: "Partial",
    1: "On Road",
    20: "Running Repair",
    30: "No Touch",
  };

  useEffect(() => {
    if (pathname.includes("notes")) {
      setActiveTab("notes");
    } else {
      setActiveTab("details");
    }
  }, [pathname]);

  useEffect(() => {
    if (etaModalVisible) {
      setEtaHours(1);
      setEtaMinutes(0);
    }
  }, [etaModalVisible]);

  const updateLocation = async () => {
    try {
      const location = await getCurrentLocation();
      if (location) {
        const { latitude, longitude } = location;
        const locationPayload = {
          Latitude: latitude,
          Longitude: longitude
        };
        await updateOldUserLocation(locationPayload);
      } else {
        // User denied permission or geolocation not supported - silently continue
      }
    } catch (error) {
      console.warn('Error updating location:', error);
    }
  };

  // Keep the status Select in sync with the mechanic's current status
  useEffect(() => {
    if (mechanicDetails?.status !== undefined && mechanicDetails?.status !== null) {
      setSelectedStatusValue(Number(mechanicDetails.status));
      const match = breakdownStatuses.find(
        (s) => Number(s.id) === Number(mechanicDetails.status),
      );
      if (match) setSelectedStatusName(match.name);
    }
  }, [mechanicDetails?.status, breakdownStatuses]);

  // Set up scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      // Show button in elevated position when user scrolls down 100px from the top
      const mainContent = document.querySelector(".main-content");
      if (mainContent && mainContent.scrollTop > 100) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    // Add event listener to main content
    const mainContent = document.querySelector(".main-content");
    if (mainContent) {
      mainContent.addEventListener("scroll", handleScroll);
    }

    // Clean up
    return () => {
      if (mainContent) {
        mainContent.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // Set current user on component mount
  useEffect(() => {
    // Get user from auth context
    const userData = localStorage.getItem("user_data");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUser(user.unique_name);
        setCurrentUserId(user.nameid);
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    } else if (authService.currentUser) {
      // Fallback to authService if available
      setCurrentUser(authService.currentUser);
    }
  }, []);

  const fetchBreakdownDetails = async () => {
    try {
      setLoading(true);

      // API URL from constants
      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.BREAKDOWNS.GET_FULL_DETAIL}/${id}`;

      // Get the token from auth_token cookie
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found in cookie");
      }

      try {
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
          },
        });

        // Set the breakdown data
        setBreakdown(response.data.breakdown);

        // Set notes data
        setNotes(response.data.notes || []);

        // Set mechanics data
        setMechanics(response.data.assignedMechanics || []);

        // Fetch image IDs for this breakdown
        await fetchBreakdownImageIds();

        // Add to recently visited breakdowns
        addRecentBreakdown(response.data.breakdown);

        updateLocation();

        setLoading(false);
      } catch (parseError) {
        console.error("Error parsing auth data:", parseError);
        throw new Error("Failed to parse authentication data");
      }
    } catch (err) {
      console.error("Error fetching breakdown details:", err);
      setError("Failed to load breakdown details");
      setLoading(false);
    }
  };

  // Fetch image IDs for the breakdown with caching support
  const fetchBreakdownImageIds = async () => {
    try {
      setLoadingImages(true);

      const breakdownId = breakdown?.id || id;

      // Clear expired cache entries periodically
      imageCacheManager.clearExpiredCache().catch(console.error);

      // Check if we have valid cached images
      const cacheIsValid = await isCacheValid(breakdownId);

      if (cacheIsValid) {
        // Get fresh image IDs to compare with cached ones
        const freshImageIds = await fetchFreshImageIds();
        const cachedData = await getCachedImages(breakdownId);

        if (cachedData && compareImageIds(cachedData.imageIds, freshImageIds)) {
          setMedia(cachedData.images);
          setLoadingImages(false);
          return;
        } else {
          console.log(
            `Cache exists but image IDs don't match - fetching fresh images`,
          );
        }
      }

      // Cache is invalid or IDs don't match - fetch fresh data
      const freshImageIds = await fetchFreshImageIds();

      // Fetch each image by ID
      if (freshImageIds && freshImageIds.length > 0) {
        await fetchMedia(freshImageIds);
      } else {
        setMedia([]);
        setLoadingImages(false);
      }
    } catch (err) {
      console.error("Error fetching image IDs:", err);
      setLoadingImages(false);
    }
  };

  // Helper function to fetch fresh image IDs from API
  const fetchFreshImageIds = async () => {
    const token = getAuthToken();

    if (!token) {
      throw new Error("No authentication token found in cookie");
    }

    // API URL for image IDs
    const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.BREAKDOWNS.GET_IMAGE_IDS}/${breakdown?.id || id}`;

    const response = await axios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "*/*",
      },
    });

    return response.data || [];
  };

  const getImageMimeFromBase64 = (b64 = "") => {
    if (b64.startsWith("iVBORw0KGgo")) return "image/png";
    if (b64.startsWith("UklGR")) return "image/webp";
    if (b64.startsWith("/9j/")) return "image/jpeg";
    return "image/jpeg";
  };

  const getDataUrl = (item) => {
    if (!item?.base64) return "";

    if (item.mediaType.startsWith("image")) {
      const mime = getImageMimeFromBase64(item.base64);
      return `data:${mime};base64,${item.base64}`;
    }

    if (item.mediaType.startsWith("video")) {
      const videoMime = item.mediaType || "video/mp4";
      return `data:${videoMime};base64,${item.base64}`;
    }

    if (item.mediaType.startsWith("pdf")) {
      return `data:application/pdf;base64,${item.base64}`;
    }

    return "";
  };

  // Fetch individual images by their IDs and cache them
  const fetchMedia = async (mediaIdList) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token found in cookie");

      const mediaPromises = mediaIdList.map(async (item) => {
        try {
          const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.BREAKDOWNS.GET_IMAGE}`;

          const response = await axios.post(
            apiUrl,
            { Id: item.id },
            {
              headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
            },
          );

          const dto = response.data || {};
          return {
            id: item.id,
            base64: dto.base64 || dto.Base64 || "",
            mediaType: (dto.mediaType || dto.MediaType || "image").toLowerCase(),
          };
        } catch (error) {
          console.warn(`Failed to fetch media ID ${item.id}:`, error.message);
          return null; // Return null for failed requests
        }
      });

      // Use Promise.allSettled instead of Promise.all to handle individual failures
      const results = await Promise.allSettled(mediaPromises);

      // Filter out rejected promises and null results, and extract values from fulfilled promises
      const fetched = results
        .filter(result => result.status === 'fulfilled' && result.value !== null)
        .map(result => result.value)
        .filter(item => item && item.base64); // Ensure we have base64 data

      setMedia(fetched);

      // Cache successfully fetched images
      if (fetched.length > 0) {
        const breakdownId = breakdown?.id || id;
        cacheImages(breakdownId, fetched, mediaIdList);
      }

      setLoadingImages(false);
    } catch (err) {
      console.error("Error in fetchMedia:", err);
      setLoadingImages(false);
    }
  };
  // Helper function to format current date and time
  const getCurrentDateTime = () => {
    const now = new Date();
    const date = now.toLocaleDateString("en-ZA", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
    const time = now.toLocaleTimeString("en-ZA", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    return `${date} ${time}`;
  };

  // Function to add location and time overlay to image
  const addLocationTimeOverlay = async (imageFile) => {
    try {
      // Get current location (null when off/denied — handled below).
      const location = await getCurrentLocation();
      const { latitude, longitude } = location ?? {};

      // Get current date and time
      const dateTime = getCurrentDateTime();

      // Create canvas to overlay text on image
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Create image element
      const img = document.createElement("img");
      const imageUrl = URL.createObjectURL(imageFile);

      return new Promise((resolve) => {
        img.onload = () => {
          // Set canvas dimensions to match image
          canvas.width = img.width;
          canvas.height = img.height;

          // Draw the original image
          ctx.drawImage(img, 0, 0);

          // Setup text styling
          const fontSize = Math.max(
            16,
            Math.min(img.width / 25, img.height / 25),
          );
          ctx.font = `${fontSize}px Arial`;
          ctx.fillStyle = "white";
          ctx.strokeStyle = "black";
          ctx.lineWidth = 2;

          // Text content - timestamp always, coordinates only when there is a fix.
          const hasFix = Number.isFinite(latitude) && Number.isFinite(longitude);
          const overlayText = [dateTime];
          if (hasFix) {
            overlayText.push(`${latitude.toFixed(6)} ${longitude.toFixed(6)}`);
          }

          // Calculate text dimensions and position
          const padding = fontSize * 0.5;
          const lineHeight = fontSize * 1.2;
          const textWidth = Math.max(
            ...overlayText.map((text) => ctx.measureText(text).width),
          );
          const textHeight = overlayText.length * lineHeight;

          // Position at bottom right
          const x = canvas.width - textWidth - padding;
          const y = canvas.height - textHeight - padding;

          // Draw semi-transparent background
          ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
          ctx.fillRect(
            x - padding / 2,
            y - padding / 2,
            textWidth + padding,
            textHeight + padding,
          );

          // Draw text with outline for better visibility
          ctx.textAlign = "left";
          ctx.textBaseline = "top";

          overlayText.forEach((text, index) => {
            const textY = y + index * lineHeight;
            // Draw stroke (outline)
            ctx.strokeStyle = "black";
            ctx.lineWidth = 3;
            ctx.strokeText(text, x, textY);
            // Draw fill (main text)
            ctx.fillStyle = "white";
            ctx.fillText(text, x, textY);
          });

          // Convert canvas to blob
          canvas.toBlob(
            (blob) => {
              // Create new file with overlay
              const overlayFile = new File([blob], imageFile.name, {
                type: imageFile.type,
                lastModified: Date.now(),
              });

              // Clean up
              URL.revokeObjectURL(imageUrl);
              resolve(overlayFile);
            },
            imageFile.type,
            0.95,
          );
        };

        img.src = imageUrl;
      });
    } catch (error) {
      console.error("Error adding overlay to image:", error);
      // Return original file if overlay fails
      return imageFile;
    }
  };

  useEffect(() => {
    if (id) {
      fetchBreakdownDetails();
      fetchBreakdownStatuses();
      loadBreakdownStatuses().then(setAllBreakdownStatuses);
    }
  }, [id]);

  // Refresh mechanic details when the component mounts
  useEffect(() => {
    if (breakdown && currentUserId && userRole === "MECHANIC") {
      fetchMechanicDetails();
    }
  }, [breakdown, currentUserId, userRole]);

  // Fetch supplier mechanic options when SUPPLIER
  useEffect(() => {
    if (userRole === "SUPPLIER" || userRole === "OWNER") {
      setMechanicOptionsLoading(true);
      getSupplierMechanicList()
        .then((data) => {
          // Data expected as array; support alt shapes
          const list = Array.isArray(data) ? data : data?.mechanics || [];
          setMechanicOptions(list || []);
        })
        .catch((err) => {
          console.error("Failed to load mechanics list:", err);
          message.error("Failed to load mechanics list");
        })
        .finally(() => setMechanicOptionsLoading(false));
    }
  }, [userRole]);

  // Fetch assigned mechanics for this breakdown (Supplier view)
  useEffect(() => {
    if (
      userRole === "SUPPLIER" ||
      (userRole === "OWNER" && (id || breakdown?.id))
    ) {
      fetchAssignedMechanics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, id, breakdown?.id]);

  // State for status update confirmation modal
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [selectedStatusValue, setSelectedStatusValue] = useState(null);
  const [selectedStatusName, setSelectedStatusName] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(false);
  const statusUpdateInFlightRef = useRef(false);

  // Handle status selection from dropdown
  const handleStatusChange = (value) => {
    // Find the status object that matches the selected value
    const selectedStatus = breakdownStatuses.find(
      (status) => Number(status.id) === Number(value),
    );
    if (selectedStatus) {
      setSelectedStatusValue(value);
      setSelectedStatusName(selectedStatus.name);

      const statusName = selectedStatus.name.toLowerCase();
      if (statusName === 'accepted' || statusName === 'en-route') {
        setEtaModalVisible(true);
      } else {
        setStatusModalVisible(true);
      }
    }
  };

  const handleMechanicChange = (value) => {
    setSelectedMechanicId(value);
  };

  const revertStatusSelection = () => {
    if (mechanicDetails?.status !== undefined && mechanicDetails?.status !== null) {
      setSelectedStatusValue(Number(mechanicDetails.status));
      const match = breakdownStatuses.find(
        (s) => Number(s.id) === Number(mechanicDetails.status),
      );
      if (match) setSelectedStatusName(match.name);
    } else {
      setSelectedStatusValue(null);
      setSelectedStatusName("");
    }
  };

  const showAssignMechanicConfirm = () => {
    if (!selectedMechanicId) return;
    setAssignConfirmVisible(true);
  };

  const handleAssignMechanic = async () => {
    try {
      if (!breakdown?.id && !id) {
        message.error("Missing breakdown information");
        return;
      }
      const selected = mechanicOptions.find(
        (m) => (m.id || m.mechanicId) === selectedMechanicId,
      );
      if (!selected) {
        message.error("Please select a mechanic");
        return;
      }

      setAssigningMechanic(true);
      await assignMechanicAndSupplier(breakdown?.id || id, {
        mechanicId: selected.id || selected.mechanicId,
        mechanicName:
          selected.fullName || selected.mechanicName || selected.name,
        supplierId: selected.supplierId,
        companyId: selected.companyId,
        companyName: selected.companyName,
      });

      message.success("Mechanic assigned successfully");
      setAssignConfirmVisible(false);
      setSelectedMechanicId(null);
      // Refresh breakdown details to reflect assignment
      await fetchBreakdownDetails();
      await fetchAssignedMechanics();
    } catch (err) {
      console.error("Failed to assign mechanic:", err);
      message.error("Failed to assign mechanic");
    } finally {
      setAssigningMechanic(false);
    }
  };

  // Fetch assigned mechanics (supplier)
  const fetchAssignedMechanics = async () => {
    try {
      if (!id && !breakdown?.id) return;
      setAssignedMechanicsLoading(true);
      const data = await getAssignedMechanics(breakdown?.id || id);
      const list = Array.isArray(data)
        ? data
        : data?.mechanics || data?.assignedMechanics || [];
      setAssignedMechanics(list || []);
    } catch (err) {
      console.error("Failed to load assigned mechanics:", err);
    } finally {
      setAssignedMechanicsLoading(false);
    }
  };

  const confirmStatusUpdate = async () => {
    if (!selectedStatusValue) return;
    if (statusUpdateInFlightRef.current || statusUpdating) return;
    statusUpdateInFlightRef.current = true;
    setStatusUpdating(true);
    try {
      const token = getAuthToken();
      if (!token || !breakdown?.id) {
        message.error("Missing required information");
        return;
      }

      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
      const mechanicId = userData.nameid;

      if (!mechanicDetails?.breakdownMechanicId) {
        message.error("Missing mechanic record information");
        return;
      }

      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.SUPPLIER.UPDATE_BREAKDOWN_MECHANIC}/${mechanicDetails.breakdownMechanicId}`;

      const requestBody = {
        mechanicModelId: mechanicId,
        status: String(selectedStatusValue),
        breakdownId: breakdown?.id,
        isMechanic: true,
      };

      // Append ETA for Accepted / En-route statuses
      const selectedStatus = breakdownStatuses.find(
        (s) => Number(s.id) === Number(selectedStatusValue),
      );
      const statusName = selectedStatus?.name?.toLowerCase() ?? '';
      if (statusName === 'accepted' || statusName === 'en-route') {
        const totalMinutes =
          (Number(etaHours) || 0) * 60 + (Number(etaMinutes) || 0);
        const etaDateTime = new Date(Date.now() + totalMinutes * 60000);
        const year = etaDateTime.getFullYear();
        const month = String(etaDateTime.getMonth() + 1).padStart(2, "0");
        const day = String(etaDateTime.getDate()).padStart(2, "0");
        const hours = String(etaDateTime.getHours()).padStart(2, "0");
        const minutes = String(etaDateTime.getMinutes()).padStart(2, "0");
        const seconds = String(etaDateTime.getSeconds()).padStart(2, "0");
        requestBody.eta = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      }

      // Append ATA when transitioning to On-site for the first time
      if (statusName === 'on-site' && !mechanicDetails?.ata) {
        const ataDateTime = new Date();
        const ataYear = ataDateTime.getFullYear();
        const ataMonth = String(ataDateTime.getMonth() + 1).padStart(2, "0");
        const ataDay = String(ataDateTime.getDate()).padStart(2, "0");
        const ataHrs = String(ataDateTime.getHours()).padStart(2, "0");
        const ataMins = String(ataDateTime.getMinutes()).padStart(2, "0");
        const ataSecs = String(ataDateTime.getSeconds()).padStart(2, "0");
        requestBody.ata = `${ataYear}-${ataMonth}-${ataDay}T${ataHrs}:${ataMins}:${ataSecs}`;
      }

      await axios.put(apiUrl, requestBody, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "*/*",
        },
      });

      updateLocation();
      message.success("Status updated successfully");
      await fetchBreakdownDetails();
    } catch (err) {
      console.error("Error updating mechanic status:", err);
      message.error("Failed to update status");
    } finally {
      setStatusUpdating(false);
      statusUpdateInFlightRef.current = false;
      setStatusModalVisible(false);
      setReasonModalVisible(false);
      setEtaModalVisible(false);
      setStatusReason("");
    }
  };

  // Handle cancellation of status update
  const cancelStatusUpdate = () => {
    setStatusModalVisible(false);
    setReasonModalVisible(false);
    setStatusReason("");
    revertStatusSelection();
  };

  // Fetch all breakdown statuses
  const fetchBreakdownStatuses = async () => {
    try {
      const statuses = await loadMechanicStatuses();
      setBreakdownStatuses(statuses);
    } catch (err) {
      console.error("Error fetching breakdown statuses:", err);
    }
  };

  const handleBack = () => {
    navigate("/dashboard");
  };

  const handleCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  // Format note text to make phone numbers clickable
  const formatNoteText = (text) => {
    if (!text) return "";

    // Regular expression to match common phone number formats
    // This regex matches patterns like: +1 234 567 8901, (123) 456-7890, 123-456-7890, 123.456.7890, etc.
    // Improved to better handle international numbers, including +27 format
    const phoneRegex =
      /(?<!\+\d*|\d)(?:\+?\d{1,3})?[\s-]?\(?\d{2,4}\)?[\s-.]?\d{3}[\s-.]?\d{4}(?!\+\d)/g;

    // Split the text by phone numbers
    const parts = text.split(phoneRegex);

    // Extract all phone numbers from the text
    const phoneMatches = text.match(phoneRegex) || [];

    // If no phone numbers found, return the original text
    if (phoneMatches.length === 0) {
      return text;
    }

    // Combine text parts with clickable phone numbers
    return (
      <>
        {parts.map((part, index) => {
          // For each part, add the text followed by a clickable phone number (if there is one)
          return (
            <React.Fragment key={index}>
              {part}
              {phoneMatches[index] && (
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    handleCall(phoneMatches[index].replace(/[^\d+]/g, "")); // Clean up the number before calling
                  }}
                  style={{
                    color: "#1890ff",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  {phoneMatches[index]}
                </a>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  // Pull to refresh handler
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchBreakdownDetails();
      message.success("Refreshed successfully");
    } catch (error) {
      console.error("Error refreshing data:", error);
      message.error("Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const showNoteModal = () => {
    form.resetFields();
    setNoteModalVisible(true);
  };

  const handleNoteSubmit = async (values) => {
    if (!values.noteText?.trim()) {
      return;
    }

    try {
      setNoteSending(true);

      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found in cookie");
      }

      // API endpoint from constants
      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.BREAKDOWNS.INSERT_NOTES}`;

      // Prepare the request payload
      const payload = {
        breakdownId: breakdown.id,
        note: values.noteText,
        type: "PWA",
      };

      // Send the request
      const response = await axios.post(apiUrl, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "*/*",
        },
      });

      if (response.data) {
        const realtimeApiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.BREAKDOWNS.UPDATE_STATUS_REALTIME}`;
        await axios.post(realtimeApiUrl, response.data.data, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        });
      }

      // Refresh the notes list by fetching the full breakdown details again
      const detailsResponse = await axios.get(
        `${API_CONFIG.BASE_URL}${API_CONFIG.BREAKDOWNS.GET_FULL_DETAIL}/${breakdown.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
          },
        },
      );

      // Update notes with the latest data
      setNotes(detailsResponse.data.notes || []);

      // Reset form and close modal
      form.resetFields();
      setNoteModalVisible(false);

      message.success("Note added successfully");

      // Scroll to the top of the notes container
      setTimeout(() => {
        // Find the notes container and scroll to its top
        const notesCard = document.querySelector(".notes-card");
        if (notesCard) {
          notesCard.scrollIntoView({ behavior: "smooth" });
        }
      }, 300);
    } catch (err) {
      console.error("Error submitting note:", err);
      message.error("Failed to add note");
    } finally {
      setNoteSending(false);
    }
  };

  // Function to fetch breakdown categories
  const fetchBreakdownCategories = async () => {
    try {
      setLoadingCategories(true);
      const rows = await breakdownCategoryService.getCategories();
      setBreakdownCategories(rows.filter((category) => category.active));
    } catch (error) {
      console.error("Error fetching breakdown categories:", error);
      message.error("Failed to load breakdown categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  // Function to fetch breakdown subcategories
  const fetchBreakdownSubcategories = async (categoryId) => {
    if (!categoryId) return;

    try {
      setLoadingSubcategories(true);
      const rows = await breakdownCategoryService.getSubCategories(categoryId);
      setBreakdownSubcategories(rows.filter((subCategory) => subCategory.active));
    } catch (error) {
      console.error("Error fetching breakdown subcategories:", error);
      message.error("Failed to load breakdown subcategories");
    } finally {
      setLoadingSubcategories(false);
    }
  };

  // Function to fetch breakdown components
  const fetchBreakdownComponents = async (subCategoryId) => {
    if (!subCategoryId) return;

    try {
      setLoadingComponents(true);
      const rows = await breakdownCategoryService.getComponents(subCategoryId);
      setBreakdownComponents(rows.filter((component) => component.active));
    } catch (error) {
      console.error("Error fetching breakdown components:", error);
      message.error("Failed to load breakdown components");
    } finally {
      setLoadingComponents(false);
    }
  };

  // Function to fetch breakdown causes
  const fetchBreakdownCauses = async () => {
    try {
      setLoadingCauses(true);
      const rows = await breakdownCategoryService.getCauses();
      setBreakdownCauses(rows.filter((cause) => cause.active));
    } catch (error) {
      console.error("Error fetching breakdown causes:", error);
      message.error("Failed to load breakdown causes");
    } finally {
      setLoadingCauses(false);
    }
  };

  // Function to show the complete breakdown modal
  const showCompleteBreakdownModal = () => {
    if (userRole !== "MECHANIC" && userRole !== "OWNER") return;
    // Fetch breakdown categories
    fetchBreakdownCategories();
    // Fetch breakdown causes
    fetchBreakdownCauses();
    // Reset form
    completeBreakdownForm.resetFields();
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedComponent(null);
    setSelectedCause(null);
    setSelectedTyres([]);
    setBreakdownSubcategories([]);
    setBreakdownComponents([]);
    setCompleteCategorySearchTerm("");
    setCompleteCategorySearchValue(undefined);
    setCompleteCategorySearchResults([]);
    // Set initial value for breakdownId
    completeBreakdownForm.setFieldsValue({
      breakdownId: breakdown?.breakdownId,
    });
    // Show modal
    setCompleteBreakdownModalVisible(true);
  };

  useEffect(() => {
    if (!completeBreakdownModalVisible) return undefined;

    const handle = window.setTimeout(() => {
      const term = completeCategorySearchTerm.trim();
      if (!term) {
        setCompleteCategorySearchResults([]);
        setCompleteCategorySearching(false);
        return;
      }

      setCompleteCategorySearching(true);
      Promise.all([
        breakdownCategoryService.searchCategoryCombinations({
          searchTerm: term,
          pageNumber: 1,
          tyres: false,
        }),
        breakdownCategoryService.searchCategoryCombinations({
          searchTerm: term,
          pageNumber: 1,
          tyres: true,
        }),
      ])
        .then(([categoryRows, tyreRows]) => {
          const combined = [...categoryRows, ...tyreRows];
          const seen = new Set();
          setCompleteCategorySearchResults(combined.filter((item) => {
            const key = buildCategorySearchValue(item);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          }));
        })
        .catch((error) => {
          console.error("Failed to search category combinations:", error);
          message.error("Failed to search category combinations");
        })
        .finally(() => setCompleteCategorySearching(false));
    }, 300);

    return () => window.clearTimeout(handle);
  }, [completeCategorySearchTerm, completeBreakdownModalVisible]);

  const handleCompleteCategorySearchSelect = (value) => {
    const item = completeCategorySearchResults.find((result) => buildCategorySearchValue(result) === value);
    if (!item) return;

    const category = {
      id: item.categoryId,
      category: item.categoryName,
      active: true,
    };
    const subCategory = {
      id: item.subCategoryId,
      categoryId: item.categoryId,
      subCategory: item.subCategoryName,
      active: true,
    };
    const component = {
      id: item.componentId,
      subCategoryId: item.subCategoryId,
      component: item.componentName,
      active: true,
    };

    setBreakdownCategories((previous) => upsertById(previous, category));
    setBreakdownSubcategories((previous) => upsertById(previous, subCategory));
    setBreakdownComponents((previous) => upsertById(previous, component));
    setSelectedCategory(category);
    setSelectedSubCategory(subCategory);
    setSelectedComponent(component);
    setSelectedCause(null);
    setSelectedTyres([]);
    setCompleteCategorySearchValue(value);
    completeBreakdownForm.setFieldsValue({
      breakdownCategory: category.id,
      breakdownSubCategory: subCategory.id,
      breakdownComponent: component.id,
      cause: undefined,
    });
    fetchBreakdownSubcategories(category.id);
    fetchBreakdownComponents(subCategory.id);
  };

  // Handle category change to fetch subcategories
  const handleCategoryChange = (value) => {
    const category = breakdownCategories.find((item) => item.id === value);
    setCompleteCategorySearchValue(undefined);
    setCompleteCategorySearchTerm("");
    setCompleteCategorySearchResults([]);
    setSelectedCategory(category || null);
    setSelectedSubCategory(null);
    setSelectedComponent(null);
    setSelectedCause(null);
    setSelectedTyres([]);
    setBreakdownComponents([]);

    // Clear any previously selected subcategory
    completeBreakdownForm.setFieldsValue({
      breakdownSubCategory: undefined,
      breakdownComponent: undefined,
      cause: undefined,
    });

    // Fetch subcategories for this category
    fetchBreakdownSubcategories(value);
  };

  const handleSubCategoryChange = (value) => {
    const subCategory = breakdownSubcategories.find((item) => item.id === value);
    setSelectedSubCategory(subCategory || null);
    setSelectedComponent(null);
    setSelectedCause(null);
    completeBreakdownForm.setFieldsValue({
      breakdownComponent: undefined,
      cause: undefined,
    });
    fetchBreakdownComponents(value);
  };

  const handleComponentChange = (value) => {
    const component = breakdownComponents.find((item) => item.id === value);
    setSelectedComponent(component || null);
    setSelectedCause(null);
    completeBreakdownForm.setFieldsValue({ cause: undefined });
  };

  const handleCauseChange = (value) => {
    const cause = breakdownCauses.find((item) => item.id === value);
    setSelectedCause(cause || null);
  };

  const toggleSelectedTyre = (position) => {
    setSelectedTyres((previous) =>
      previous.includes(position)
        ? previous.filter((item) => item !== position)
        : [...previous, position].sort((left, right) => left - right)
    );
  };

  // Handle complete breakdown form submission
  const handleCompleteBreakdownSubmit = (values) => {
    if (selectedCategory?.category?.toUpperCase() === "TYRES" && selectedTyres.length === 0) {
      message.error("Please select tyre position(s)");
      setTyrePickerVisible(true);
      return;
    }

    // Store form values for final submission
    setFormData({
      ...values,
      tyrePosition: selectedTyres.join(","),
    });

    // Show confirmation dialog
    setConfirmCompleteModalVisible(true);

    // Hide the form modal
    setCompleteBreakdownModalVisible(false);
  };

  // Handle final submission after confirmation
  const handleFinalSubmit = async () => {
    try {
      setSubmitLoading(true);

      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found in cookie");
      }

      if (!selectedCategory || !selectedSubCategory || !selectedComponent) {
        throw new Error("Category, sub-category, and component are required");
      }

      await breakdownCategoryService.insertBreakdownCategory({
        breakdownId: breakdown?.id,
        categoryId: selectedCategory.id,
        categoryName: selectedCategory.category,
        subCategoryId: selectedSubCategory.id,
        subCategoryName: selectedSubCategory.subCategory,
        componentId: selectedComponent.id,
        componentName: selectedComponent.component,
        causeId: selectedCause?.id || undefined,
        causeName: selectedCause?.cause || undefined,
        tyrePosition: selectedCategory.category?.toUpperCase() === "TYRES"
          ? formData.tyrePosition
          : "0",
      });

      const payload = {
        breakdownId: breakdown?.id,
        breakdownCategory: "",
        breakdownSubCategory: "",
        correction: formData.correction,
        faultyAsset: formData.affectedAsset,
        breakdownMechanicId: mechanicDetails?.breakdownMechanicId,
        cause: selectedCause?.cause || null,
        ...(selectedCategory.category?.toUpperCase() === "TYRES" && {
          tyrePosition: formData.tyrePosition,
        }),
        type: "PWA",
      };

      const response = await axios.post(
        `${API_CONFIG.BASE_URL}${API_CONFIG.REPAIR_ACTIONS.FINISH_BREAKDOWN}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "*/*",
          },
        },
      );

      updateLocation();

      if (response.status === 200 || response.status === 201) {
        // Remove from recent breakdowns list
        if (breakdown?.id) {
          // Make sure to convert the ID to string for consistent comparison
          removeRecentBreakdown(String(breakdown.id));
        }

        // Show success notification with longer duration
        notification.success({
          message: "Breakdown Completed",
          description:
            "The breakdown has been successfully completed and marked as resolved.",
          duration: 5,
          placement: "topRight",
        });

        // Close the confirmation modal
        setConfirmCompleteModalVisible(false);

        // Refresh breakdown details to show updated status
        await fetchBreakdownDetails();

        // Redirect to dashboard after a short delay to allow the user to see the success message
        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } else {
        throw new Error(`API returned status code ${response.status}`);
      }
    } catch (error) {
      console.error("Error completing breakdown:", error);
      message.error("Failed to complete breakdown. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Fetch a single image by ID and append to the existing media array
  const fetchSingleMedia = async (mediaId) => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token found in cookie");

      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.BREAKDOWNS.GET_IMAGE}`;

      try {
        const response = await axios.post(
          apiUrl,
          { Id: mediaId },
          {
            headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
          }
        );

        const dto = response.data || {};
        const newMediaItem = {
          id: mediaId,
          base64: dto.base64 || dto.Base64 || "",
          mediaType: (dto.mediaType || dto.MediaType || "image").toLowerCase(),
        };

        if (newMediaItem.base64) {
          // Append to existing media array
          setMedia(prevMedia => [...prevMedia, newMediaItem]);

          // Update cache with the new image
          const breakdownId = breakdown?.id || id;
          const allMediaIds = [...media.map(m => ({ id: m.id })), { id: mediaId }];
          cacheImages(breakdownId, [...media, newMediaItem], allMediaIds);

          return true;
        }
        return false;
      } catch (error) {
        console.warn(`Failed to fetch newly uploaded media ID ${mediaId}:`, error.message);
        return false;
      }
    } catch (err) {
      console.error("Error in fetchSingleMedia:", err);
      return false;
    }
  };

  const handleMediaUpload = async (file) => {
    try {
      setUploadingImage(true);

      const token = getAuthToken();
      if (!token) throw new Error("No authentication token found in cookie");

      const mediaType = getMediaTypeFromFile(file);

      let processedFile = file;

      // Only do overlay for images
      if (mediaType.startsWith("image")) {
        const now = Date.now();
        const fileAge = now - file.lastModified;
        const imageMinutes = 30;
        const isCameraImage = fileAge < imageMinutes * 60 * 1000;

        if (isCameraImage) {
          try {
            message.loading("Adding location and time to image...", 0);
            processedFile = await addLocationTimeOverlay(file);
            message.destroy();
          } catch (overlayError) {
            message.destroy();
            processedFile = file;
          }
        }
      }

      const base64 = await readFileAsBase64(processedFile);

      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.BREAKDOWNS.ADD_IMAGE}`;

      const payload = [
        {
          BreakdownId: breakdown.id,
          Image: base64,
          MediaType: mediaType || EXT_TO_MIME[String(processedFile.name.split(".").pop())],
          Extension: processedFile.name.split(".").pop(),
          LastModifiedOn: null,
          Deleted: null,
          DeletedOn: null,
          CreatedOn: null,
        },
      ];

      // Upload the image and get the response
      const response = await axios.post(apiUrl, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "*/*",
        },
      });

      // Check if the API returned the ID of the newly created media
      let newMediaId = null;

      // Different API response formats handling
      if (response.data) {
        if (Array.isArray(response.data) && response.data[0]?.id) {
          // Format: [{ id: 'xxx', ... }]
          newMediaId = response.data[0].id;
        } else if (response.data.id) {
          // Format: { id: 'xxx', ... }
          newMediaId = response.data.id;
        } else if (Array.isArray(response.data) && response.data[0]?.Id) {
          // Format: [{ Id: 'xxx', ... }]
          newMediaId = response.data[0].Id;
        } else if (response.data.Id) {
          // Format: { Id: 'xxx', ... }
          newMediaId = response.data.Id;
        } else if (response.data.imageId || response.data.ImageId) {
          // Format: { imageId: 'xxx', ... }
          newMediaId = response.data.imageId || response.data.ImageId;
        }
      }

      // If we got the ID of the new media, fetch just that one
      if (newMediaId) {
        await fetchSingleMedia(newMediaId);
      } else {
        // Fallback: If we couldn't get the ID, fetch all images
        // This is less efficient but ensures the UI stays in sync
        console.log("Could not determine new media ID, fetching all images");
        await fetchBreakdownImageIds();
      }

      message.success(
        mediaType === "image"
          ? "Image uploaded successfully"
          : mediaType === "video"
            ? "Video uploaded successfully"
            : "PDF uploaded successfully",
      );

      return true;
    } catch (err) {
      console.error("Error uploading media:", err);
      message.error("Failed to upload media");
      return false;
    } finally {
      setUploadingImage(false);
    }
  };
  const getMediaTypeFromFile = (file) => {
    return file.type;
  };

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        // result is "data:...;base64,XXXX"
        resolve(result.includes(",") ? result.split(",")[1] : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Fetch vehicle history data
  const fetchVehicleHistory = async () => {
    try {
      setLoadingHistory(true);

      // Get token from auth_token cookie
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found in cookie");
      }

      // API URL for vehicle history
      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.BREAKDOWNS.GET_HISTORY}`;

      // Parameters needed: breakdownId, registrationNumber, etc.
      const payload = {
        breakdownId: breakdown?.id,
        registrationNumber: breakdown?.registrationNumber,
        trailerRegistrationNumber: "",
        fleetNumber: breakdown?.fleetNumber,
      };

      const response = await axios.post(apiUrl, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Set vehicle history data
      setVehicleHistory(response.data || []);
      setLoadingHistory(false);
    } catch (err) {
      console.error("Error fetching vehicle history:", err);
      setLoadingHistory(false);
    }
  };

  // Fetch vehicle history when the breakdown data is loaded
  useEffect(() => {
    if (breakdown && breakdown.registrationNumber) {
      fetchVehicleHistory();
    }
  }, [breakdown]);

  // Fetch mechanic details including status
  const fetchMechanicDetails = async () => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found in cookie");
      }

      if (!token || !breakdown?.id) {
        return;
      }

      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.BREAKDOWNS.GET_MECHANIC_DETAILS}/${breakdown.id}`;

      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
        },
      });

      // Find the mechanic that matches the current user's ID
      if (response.data && Array.isArray(response.data)) {
        const currentMechanic = response.data.find(
          (mechanic) => mechanic.id === currentUserId,
        );
        if (currentMechanic) {
          setMechanicDetails(currentMechanic);
        }
      }
    } catch (err) {
      console.error("Error fetching mechanic details:", err);
    }
  };

  // Show acceptance confirmation modal
  const showAcceptConfirm = () => {
    setConfirmModalType("accept");
    setConfirmModalVisible(true);
  };

  // Show decline confirmation modal
  const showDeclineConfirm = () => {
    setConfirmModalType("decline");
    setConfirmModalVisible(true);
  };

  // Handle modal confirmation
  const handleModalConfirm = async () => {
    setConfirmModalLoading(true);
    if (confirmModalType === "accept") {
      await performAcceptBreakdown();
    } else if (confirmModalType === "decline") {
      await performDeclineBreakdown();
    }
    setConfirmModalVisible(false);
    setConfirmModalLoading(false);
  };

  // Handle modal cancellation
  const handleModalCancel = () => {
    setConfirmModalVisible(false);
  };

  // Perform the actual acceptance action
  const performAcceptBreakdown = async () => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found in cookie");
      }

      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");

      if (!token || !breakdown?.id || !mechanicDetails) {
        message.error("Missing required information");
        return;
      }

      const fullName = userData.unique_name;
      const mechanic = mechanics.find(
        (mechanic) => currentUserId === mechanic.mechanicModelId,
      );

      // Get current position using our location utility
      const { latitude, longitude } = await getCurrentLocation();

      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.BREAKDOWNS.ACCEPT_MECHANIC_STATUS}`;
      const payload = {
        breakdownId: breakdown.id,
        breakdownMechanicId: mechanic.id,
        mechanicName: fullName,
        mechanicMobile: mechanicDetails.mobileNum,
        latitude: latitude.toString(),
        longitude: longitude.toString(),
        type: "PWA",
      };

      const response = await axios.post(apiUrl, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
        },
      });

      message.success("Breakdown accepted successfully");

      // Refresh mechanic details
      await fetchMechanicDetails();

      // Refresh the breakdown details
      await fetchBreakdownDetails();
    } catch (err) {
      console.error("Error accepting breakdown:", err);
      if (err.message.includes("Geolocation")) {
        message.error(
          "Unable to access location. Please check your browser permissions.",
        );
      } else {
        message.error("Failed to accept breakdown");
      }
    }
  };

  // Perform the actual decline action
  const performDeclineBreakdown = async () => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error("No authentication token found in cookie");
      }

      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");

      if (!token || !breakdown?.id || !mechanicDetails) {
        message.error("Missing required information");
        return;
      }

      // Extract needed data
      const mechanicId = userData.nameid;
      const loginId = userData.LoginId;
      const userName = userData.unique_name;
      const fullName = userData.unique_name;
      const companyName = mechanicDetails.companyName;

      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.BREAKDOWNS.UPDATE_MECHANIC_STATUS}/${breakdown.id}`;
      const params = new URLSearchParams({
        mechanicId,
        status: 1, // 1 for Reject
        loginId,
        UserId: mechanicId,
        userName,
        fullName,
        companyName,
        type: "PWA",
      });

      const response = await axios.post(`${apiUrl}?${params.toString()}`, "", {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
        },
      });

      message.success("Breakdown declined successfully");

      // Refresh mechanic details
      await fetchMechanicDetails();

      // Refresh the breakdown details
      await fetchBreakdownDetails();
    } catch (err) {
      console.error("Error declining breakdown:", err);
      message.error("Failed to decline breakdown");
    }
  };

  // Function to open video modal
  const openVideoModal = (src, title = 'Video') => {
    setCurrentVideoSrc(src);
    setCurrentVideoTitle(title);
    setVideoModalVisible(true);
  };

  if (error) {
    return (
      <div className="breakdown-details-container">
        <Result
          status="error"
          title="Failed to load breakdown details. Check network connection."
          subTitle={error || "Could not find the requested breakdown"}
          extra={[
            <Button type="primary" key="back" onClick={handleBack}>
              Back to Dashboard
            </Button>,
          ]}
        />
      </div>
    );
  }

  if (loading && !breakdown) {
    return (
      <div className="breakdown-details-container">
        <div className="details-loading">
          <Spin size="large" tip="Loading breakdown details..." />
        </div>
      </div>
    );
  }

  const handleShare = async (label, text) => {
    const url = `${shareUrlBase}/breakdown/share/${breakdown.breakdownReference}`;

    try {
      if (isInIframe) {
        setIsShare(true);
        setRestriction(true);
        return;
      }

      if (navigator.share) {
        await navigator.share({
          title: label,
          text: text,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        message.success("Link copied.");
      }
    } catch (err) {
      console.warn("Share cancelled or failed:", err);
      message.error("Share cancelled or failed.");
    }
  };

  // Determine status text for the overall breakdown status display
  const getStatusName = (statusValue) => {
    if (statusValue === undefined || statusValue === null) {
      return breakdown?.statusDescription || "Unknown";
    }

    const matchedStatus = allBreakdownStatuses.find(
      (status) => Number(status.value) === Number(statusValue),
    );
    return matchedStatus
      ? matchedStatus.name
      : breakdown?.statusDescription || "Unknown";
  };

  const statusText = getStatusName(breakdown?.breakdownStatus);
  const statusColor =
    BREAKDOWN_STATUS_COLORS[breakdown?.breakdownMechanicStatus] || "#999";
  const formattedDate = breakdown?.dateTimeOffset
    ? formatDate(breakdown.dateTimeOffset)
    : "Unknown";

  // Derived selection for modal display
  const selectedMechanic = mechanicOptions.find(
    (m) => (m.id || m.mechanicId) === selectedMechanicId,
  );

  // Derived: filter out already assigned mechanics from dropdown
  const assignedMechanicIds = new Set(
    (assignedMechanics || []).map(
      (m) => m.mechanicId ?? m.id ?? m.mechanicModelId ?? m.userId,
    ),
  );
  const filteredMechanicOptions = mechanicOptions.filter(
    (m) => !assignedMechanicIds.has(m.id ?? m.mechanicId),
  );

  return (
    <div className="breakdown-details-container">
      <PullToRefresh onRefresh={handleRefresh}>
        {refreshing && (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              paddingBottom: "16px",
            }}
          >
            <Spin size="large" />
          </div>
        )}
        <Card className="ref-status-card">
          <div className="breakdown-ref-status">
            <Text strong className="details-ref">
              {breakdown?.breakdownReference || "Unknown Reference"}
            </Text>
            <Badge color={statusColor} text={statusText} />
          </div>
          {mechanicDetails && (
            <>
              <div
                className="mechanic-status"
                style={{
                  marginTop: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text type="secondary">Your Status:</Text>
                <Badge
                  color={
                    BREAKDOWN_STATUS_COLORS[mechanicDetails.status] || "#999"
                  }
                  text={
                    breakdownStatuses.find(s => Number(s.id) === Number(mechanicDetails.status))?.name ||
                    "Unknown"
                  }
                />
              </div>

              {/* Show Accept/Decline buttons only if mechanic status is Pending */}
              {userRole === "MECHANIC" && breakdownStatuses.find(s => Number(s.id) === Number(mechanicDetails.status))?.name?.toLowerCase() === "pending" && (
                <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    style={{ flex: 1 }}
                    onClick={showAcceptConfirm}
                  >
                    Accept
                  </Button>
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    style={{ flex: 1 }}
                    onClick={showDeclineConfirm}
                  >
                    Decline
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>

        {/* Tabbed Interface */}
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className="breakdown-tabs"
          tabBarGutter={18}
        >
          {/* Details Tab */}
          <TabPane
            tab={
              <span>
                <InfoCircleOutlined /> Details
              </span>
            }
            key="details"
          >
            <Card className="details-card">
              <Collapse
                defaultActiveKey={["1"]}
                className="detail-collapse"
                bordered={false}
              >
                <Collapse.Panel header="Vehicle Information" key="1">
                  <Descriptions column={1} bordered>
                    <Descriptions.Item label="Registration">
                      <Space>
                        <CarOutlined />
                        {breakdown?.registrationNumber || "N/A"}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Fleet">
                      {breakdown?.fleetNumber || "N/A"}
                    </Descriptions.Item>
                    <Descriptions.Item label="VIN">
                      {breakdown?.vinNumber || "N/A"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Make & Model">
                      {breakdown?.make || "Unknown"} {breakdown?.model || ""}{" "}
                      {breakdown?.year || ""}
                    </Descriptions.Item>
                    <Descriptions.Item label="Company">
                      {breakdown?.companyName || "Unknown"}
                    </Descriptions.Item>
                  </Descriptions>
                </Collapse.Panel>

                <Collapse.Panel header="Breakdown Information" key="2">
                  <Descriptions column={1} bordered>
                    <Descriptions.Item label="Type">
                      {breakdownTypeMap[breakdown.breakdownType] || "Unknown"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Description">
                      {breakdown?.description ||
                        breakdown?.faultDescription ||
                        "No description available"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Address">
                      <Space
                        align="start"
                        direction="vertical"
                        style={{ width: "100%" }}
                      >
                        <Space>
                          <EnvironmentOutlined />
                          <Text>
                            {breakdown?.breakdownAddress ||
                              "Address not available"}
                          </Text>
                        </Space>
                        {breakdown?.latitude && breakdown?.longitude && (
                          <Button
                            type="primary"
                            icon={<EnvironmentOutlined />}
                            onClick={() => {
                              const lat = breakdown.latitude;
                              const lng = breakdown.longitude;

                              // Try using the universal geo: protocol first, which should open the default map app on most devices
                              // Fall back to different methods if this fails
                              const tryOpenMap = (mapUrl) => {
                                const mapLink = document.createElement("a");
                                mapLink.href = mapUrl;
                                mapLink.target = "_blank";

                                // Simulate a click event to open the URL
                                const clickEvent =
                                  document.createEvent("MouseEvents");
                                clickEvent.initEvent("click", true, true);
                                const didOpen =
                                  mapLink.dispatchEvent(clickEvent);

                                return didOpen;
                              };

                              // Check platforms for better fallback handling
                              const isIOS =
                                /iPad|iPhone|iPod/.test(navigator.userAgent) &&
                                !window.MSStream;
                              const isAndroid = /android/i.test(
                                navigator.userAgent,
                              );
                              const isHuawei = /huawei/i.test(
                                navigator.userAgent,
                              );
                              const isMobile =
                                isIOS ||
                                isAndroid ||
                                isHuawei ||
                                /mobile/i.test(navigator.userAgent);

                              let opened = false;

                              // For iOS, use Apple Maps directly with coordinates
                              if (isIOS) {
                                // Try Apple Maps (maps://) first for iOS devices
                                opened = tryOpenMap(
                                  `maps://?ll=${lat},${lng}&q=${lat},${lng}`,
                                );

                                // If Apple Maps fails, try Google Maps iOS app
                                if (!opened) {
                                  opened = tryOpenMap(
                                    `comgooglemaps://?center=${lat},${lng}&q=${lat},${lng}`,
                                  );
                                }
                              }
                              // For other mobile devices, try the universal geo: protocol with coordinates
                              else if (isMobile) {
                                opened = tryOpenMap(
                                  `geo:${lat},${lng}?q=${lat},${lng}`,
                                );
                              }

                              if (!opened && isHuawei) {
                                opened = tryOpenMap(
                                  `petalmaps://navigation?dlat=${lat}&dlon=${lng}`,
                                );
                              }

                              // Final fallback to web-based maps with coordinates
                              if (!opened) {
                                if (isHuawei) {
                                  // Huawei devices may prefer Petal Maps web version
                                  window.open(
                                    `https://maps.hmxt.com/bdcmap/place/search?query=${lat},${lng}`,
                                    "_blank",
                                  );
                                } else {
                                  // Google Maps as last resort - using coordinates
                                  window.open(
                                    `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
                                    "_blank",
                                  );
                                }
                              }
                            }}
                            style={{ alignItems: "unset" }}
                            size="small"
                          >
                            Navigate
                          </Button>
                        )}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Driver">
                      {breakdown?.driverAlias || "Unknown"}
                    </Descriptions.Item>
                    {breakdown?.driverCell && (
                      <Descriptions.Item label="Contact">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            flexDirection: "column",
                            gap: 8,
                          }}
                        >
                          <div>
                            <Button
                              type="primary"
                              icon={<PhoneOutlined />}
                              onClick={() => {
                                if (isInIframe) {
                                  setIsShare(false);
                                  setRestriction(true);
                                  return;
                                }
                                showCallOptionsModal(
                                  breakdown.driverCell,
                                  `Driver: ${breakdown.driverAlias || "Unknown"}`,
                                );
                              }}
                              size="small"
                            >
                              Call Driver
                            </Button>
                          </div>
                          <div>
                            <Text>{breakdown.driverCell}</Text>
                          </div>
                        </div>
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Share">
                      <Space>
                        <Button
                          type="primary"
                          icon={<ShareAltOutlined />}
                          onClick={() =>
                            handleShare(
                              `Breakdown ${breakdown?.breakdownReference}`,
                              `Breakdown: ${breakdown?.description || breakdown?.faultDescription || "No description available"}`,
                            )
                          }
                          size="small"
                        >
                          Share
                        </Button>
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Reported">
                      <Space>
                        <ClockCircleOutlined />
                        {formattedDate}
                      </Space>
                    </Descriptions.Item>
                  </Descriptions>
                </Collapse.Panel>

                {(userRole === "SUPPLIER" ||
                  (userRole === "OWNER" &&
                    filteredMechanicOptions.length > 0)) && (
                    <Collapse.Panel header="Mechanics" key="4">
                      {assignedMechanicsLoading ? (
                        <div style={{ textAlign: "center", padding: "8px 0" }}>
                          <Spin />
                          <div style={{ marginTop: 8 }}>
                            <Text type="secondary">
                              Loading assigned mechanics...
                            </Text>
                          </div>
                        </div>
                      ) : assignedMechanics && assignedMechanics.length > 0 ? (
                        <List
                          dataSource={assignedMechanics}
                          renderItem={(am, idx) => {
                            const statusVal = parseInt(
                              am.status ??
                              am.mechanicStatus ??
                              am.statusValue ??
                              am.breakdownMechanicStatus,
                              10,
                            );
                            const statusColor =
                              BREAKDOWN_STATUS_COLORS[statusVal] || "#999";
                            const statusText =
                              breakdownStatuses.find(s => Number(s.id) === Number(statusVal))?.name || "Unknown";
                            const name =
                              am.mechanicName ||
                              am.fullName ||
                              am.name ||
                              am.mechanic ||
                              am.userName ||
                              "Unknown";
                            return (
                              <List.Item
                                key={idx}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Space>
                                  <UserOutlined />
                                  <Text strong>{name}</Text>
                                  {am.companyName ? (
                                    <Text type="secondary">
                                      • {am.companyName}
                                    </Text>
                                  ) : null}
                                </Space>
                                <Badge color={statusColor} text={statusText} />
                              </List.Item>
                            );
                          }}
                        />
                      ) : (
                        <Empty description="No mechanics assigned yet" />
                      )}
                      <Divider style={{ margin: "16px 0" }} />
                      <Paragraph>
                        Select a mechanic to assign to this breakdown:
                      </Paragraph>
                      <Select
                        style={{ width: "100%" }}
                        placeholder="Select mechanic"
                        onChange={handleMechanicChange}
                        value={selectedMechanicId}
                        loading={mechanicOptionsLoading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.children || "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {filteredMechanicOptions.map((m) => (
                          <Select.Option
                            key={m.id || m.mechanicId}
                            value={m.id || m.mechanicId}
                          >
                            {(m.fullName || m.mechanicName || m.name) +
                              (m.companyName ? ` • ${m.companyName}` : "")}
                          </Select.Option>
                        ))}
                      </Select>

                      <Divider style={{ margin: "16px 0" }} />
                      <Button
                        type="primary"
                        onClick={showAssignMechanicConfirm}
                        style={{ width: "100%" }}
                        disabled={!selectedMechanicId || assigningMechanic}
                        loading={assigningMechanic}
                      >
                        Assign Mechanic
                      </Button>
                    </Collapse.Panel>
                  )}

                <Collapse.Panel header="Vehicle History" key="3">
                  {loadingHistory ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "20px",
                      }}
                    >
                      <Spin tip="Loading vehicle history..." />
                    </div>
                  ) : vehicleHistory && vehicleHistory.length > 0 ? (
                    <List
                      dataSource={vehicleHistory}
                      renderItem={(breakdown) => (
                        <List.Item>
                          <BreakdownCard
                            breakdown={breakdown}
                            hideStatus={true}
                            clickable={false}
                          />
                        </List.Item>
                      )}
                    />
                  ) : (
                    <Empty description="No vehicle history available" />
                  )}
                </Collapse.Panel>
              </Collapse>
            </Card>
          </TabPane>

          {/* Notes Tab */}
          <TabPane
            tab={
              <span>
                <MessageOutlined /> Notes
              </span>
            }
            key="notes"
          >
            <Card className="notes-card">
              {/* Notes Display - WhatsApp style */}
              <div className="notes-container">
                {notes && notes.length > 0 ? (
                  // Sort notes from newest to oldest
                  [...notes]
                    .sort(
                      (a, b) =>
                        new Date(b.createdDateTime) -
                        new Date(a.createdDateTime),
                    )
                    .map((note, index) => {
                      const isCurrentUser = note.userName === currentUser;
                      const noteDate = formatDate(note.createdDateTime);
                      const isFollowUp = note.isFollow === 1;
                      const isStatusUpdate = note.recordType === "S";

                      return (
                        <div
                          key={index}
                          className={`note-bubble ${isCurrentUser ? "note-self" : "note-other"} ${isStatusUpdate ? "note-status" : ""}`}
                        >
                          {!isStatusUpdate && (
                            <div className="note-header">
                              <Text strong>
                                {isCurrentUser ? "You" : note.userName}
                              </Text>
                            </div>
                          )}
                          <div className="note-content">
                            <Text>
                              {isStatusUpdate
                                ? `Status: ${note.note}`
                                : formatNoteText(note.note)}
                            </Text>
                          </div>
                          <div className="note-footer">
                            <Text type="secondary" className="note-time">
                              {noteDate}
                            </Text>
                            {isFollowUp && (
                              <Badge
                                color="red"
                                text="Follow-up"
                                className="follow-up-badge"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <div className="no-notes">
                    <Text type="secondary">
                      No notes available for this breakdown.
                    </Text>
                  </div>
                )}
                {/* Reference for auto-scrolling to the newest note */}
                <div ref={messagesEndRef} />
              </div>
            </Card>
          </TabPane>

          {/* Images Tab */}
          <TabPane
            tab={
              <span>
                <PictureOutlined /> Media
              </span>
            }
            key="images"
          >
            <Card className="images-card">
              {/* Image Upload */}
              <div className="image-upload-section">
                <Upload
                  accept="image/*,video/*,application/pdf"
                  listType="picture-card"
                  customRequest={({ file, onSuccess, onError }) => {
                    handleMediaUpload(file)
                      .then(() => onSuccess("ok"))
                      .catch((error) => onError(error));
                  }}
                  showUploadList={false}
                  multiple
                >
                  {uploadingImage ? (
                    <div>
                      <Spin />
                      <div style={{ marginTop: 8 }}>Uploading...</div>
                    </div>
                  ) : (
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>
                        Upload From Gallery/Camera
                      </div>
                    </div>
                  )}
                </Upload>
              </div>

              {/* Images Display */}
              <div className="images-container">
                {loadingImages ? (
                  <div className="loading-images">
                    <Spin size="large" />
                    <Text type="secondary" style={{ marginTop: "16px" }}>
                      Loading images...
                    </Text>
                  </div>
                ) : media && media.length > 0 ? (
                  <div
                    className="image-grid"
                    style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
                  >
                    {media.map((m, index) => {
                      const src = getDataUrl(m);

                      if (m.mediaType.startsWith("image")) {
                        return (
                          <Image
                            key={m.id}
                            width={90}
                            height={90}
                            style={{ objectFit: "cover" }}
                            src={src}
                            alt={`Media ${index + 1}`}
                          />
                        );
                      }

                      if (m.mediaType.startsWith("video")) {
                        return (
                          <div
                            key={m.id}
                            style={{
                              width: 90,
                              height: 90,
                              borderRadius: 8,
                              overflow: "hidden",
                              background: "#000",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              position: "relative",
                            }}
                            onClick={() => openVideoModal(src, `Video ${index + 1}`)}
                          >
                            <video
                              src={src}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              preload="metadata"
                              playsInline
                              muted
                            />
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              backgroundColor: 'rgba(0,0,0,0.6)',
                              borderRadius: '50%',
                              width: 36,
                              height: 36,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '2px solid white',
                            }}>
                              <PlayCircleOutlined style={{ color: 'white', fontSize: 20 }} />
                            </div>
                          </div>
                        );
                      }

                      if (m.mediaType.startsWith("pdf")) {
                        return (
                          <a
                            key={m.id}
                            href={src}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              width: 90,
                              height: 90,
                              borderRadius: 8,
                              border: "1px solid #e5e5e5",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexDirection: "column",
                              textDecoration: "none",
                            }}
                          >
                            <FileImageOutlined
                              style={{ fontSize: 28, color: "#d32029" }}
                            />
                            <div
                              style={{
                                fontSize: 11,
                                marginTop: 6,
                                color: "#555",
                              }}
                            >
                              PDF
                            </div>
                          </a>
                        );
                      }

                      return null;
                    })}
                  </div>
                ) : (
                  <div className="no-images">
                    <FileImageOutlined
                      style={{ fontSize: "48px", color: "#ccc" }}
                    />
                    <Text type="secondary" style={{ marginTop: "16px" }}>
                      No images available. Upload images using the button above.
                    </Text>
                  </div>
                )}
              </div>
            </Card>
          </TabPane>

          {/* Status Tab */}
          <TabPane
            tab={
              <span>
                <ReloadOutlined /> Status
              </span>
            }
            key="status"
          >
            <Card className="status-card">
              <>
                <Title level={5}>Update Your Status</Title>
                <Paragraph>Select your current status for this breakdown:</Paragraph>

                {breakdownStatuses && breakdownStatuses.length > 0 ? (
                  <div style={{ marginTop: "16px" }}>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select status"
                      onChange={handleStatusChange}
                      value={selectedStatusValue}
                    >
                      {breakdownStatuses
                        .filter(
                          (status) =>
                            status.name.toLowerCase() !== 'remove',
                        )
                        .map((status) => (
                          <Select.Option
                            key={status.id}
                            value={Number(status.id)}
                          >
                            {status.name}
                          </Select.Option>
                        ))}
                    </Select>
                    {mechanicDetails && (
                      <div style={{ marginTop: "16px" }}>
                        <Text type="secondary">
                          Your current status:{" "}
                          <Badge
                            color={BREAKDOWN_STATUS_COLORS[mechanicDetails.status] || "#999"}
                            text={breakdownStatuses.find(s => Number(s.id) === Number(mechanicDetails.status))?.name || "Unknown"}
                          />
                        </Text>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <Spin />
                    <div style={{ marginTop: "8px" }}>
                      <Text type="secondary">
                        Loading available statuses...
                      </Text>
                    </div>
                  </div>
                )}

                {/* Complete Breakdown Button */}
                <Divider style={{ margin: "24px 0 16px" }} />
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={showCompleteBreakdownModal}
                  style={{ width: "100%" }}
                >
                  Complete Breakdown
                </Button>
              </>
            </Card>
          </TabPane>
        </Tabs>
      </PullToRefresh>

      {/* Floating Action Button for adding notes */}
      {activeTab === "notes" && (
        <div
          className={`floating-action-button ${isScrolled ? "elevated" : ""}`}
        >
          <Button
            type="primary"
            shape="circle"
            icon={<EditOutlined />}
            onClick={showNoteModal}
          />
        </div>
      )}

      {/* Note Modal */}
      <Modal
        title="Add Note"
        open={noteModalVisible}
        onCancel={() => setNoteModalVisible(false)}
        footer={null}
      >
        <Form form={form} onFinish={handleNoteSubmit}>
          <Form.Item
            name="noteText"
            rules={[{ required: true, message: "Please enter a note" }]}
          >
            <TextArea
              rows={4}
              placeholder="Type your note here..."
              maxLength={500}
              showCount
            />
          </Form.Item>
          <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
            <Button
              type="default"
              onClick={() => setNoteModalVisible(false)}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              loading={noteSending}
            >
              Send
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Status Update Confirmation Modal */}
      <Modal
        title="Confirm Status Change"
        open={statusModalVisible}
        onOk={confirmStatusUpdate}
        onCancel={cancelStatusUpdate}
        okText="Confirm"
        cancelText="Cancel"
        confirmLoading={statusUpdating}
        okButtonProps={{ disabled: statusUpdating }}
      >
        <p>
          Are you sure you want to change the status to{" "}
          <strong>{selectedStatusName}</strong>?
        </p>
        <p>
          This will update the breakdown status and notify all relevant parties.
        </p>
      </Modal>

      {/* ETA Modal for En-Route Status */}
      <Modal
        title="Set Your ETA"
        open={etaModalVisible}
        onOk={() => {
          setEtaModalVisible(false);
          setStatusModalVisible(true);
        }}
        onCancel={() => {
          setEtaModalVisible(false);
          revertStatusSelection();
          setStatusReason("");
        }}
        okText="Continue"
        cancelText="Cancel"
        okButtonProps={{
          disabled: (etaHours ?? 0) === 0 && (etaMinutes ?? 0) === 0,
        }}
        width={window.innerWidth < 768 ? "95%" : 300}
        destroyOnHidden
      >
        <div style={{ textAlign: "center" }}>
          <p style={{ marginBottom: "16px", fontSize: "16px" }}>
            Please enter how long until you arrive in hours and minutes.
          </p>
          <Space
            align="center"
            style={{ width: "100%", justifyContent: "center" }}
          >
            <div>
              <InputNumber
                min={0}
                max={72}
                value={etaHours}
                onChange={(val) => setEtaHours(Number(val ?? 0))}
                precision={0}
                inputMode="numeric"
                parser={(val) => (val || "").replace(/[^0-9]/g, "")}
                onKeyDown={(e) => {
                  const allowed = [
                    "Backspace",
                    "Delete",
                    "Tab",
                    "Escape",
                    "Enter",
                    "ArrowLeft",
                    "ArrowRight",
                    "Home",
                    "End",
                  ];
                  if (allowed.includes(e.key)) return;
                  if (
                    (e.ctrlKey || e.metaKey) &&
                    ["a", "c", "v", "x"].includes(e.key.toLowerCase())
                  )
                    return;
                  if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                }}
                onPaste={(e) => {
                  const text = (e.clipboardData?.getData("text") || "").trim();
                  if (!/^\d+$/.test(text)) e.preventDefault();
                }}
                style={{ width: 90 }}
              />
              <div style={{ fontSize: 12, marginTop: 4 }}>Hours</div>
            </div>
            <div>
              <InputNumber
                min={0}
                max={59}
                step={5}
                value={etaMinutes}
                onChange={(val) => setEtaMinutes(Number(val ?? 0))}
                precision={0}
                inputMode="numeric"
                parser={(val) => (val || "").replace(/[^0-9]/g, "")}
                onKeyDown={(e) => {
                  const allowed = [
                    "Backspace",
                    "Delete",
                    "Tab",
                    "Escape",
                    "Enter",
                    "ArrowLeft",
                    "ArrowRight",
                    "Home",
                    "End",
                  ];
                  if (allowed.includes(e.key)) return;
                  if (
                    (e.ctrlKey || e.metaKey) &&
                    ["a", "c", "v", "x"].includes(e.key.toLowerCase())
                  )
                    return;
                  if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                }}
                onPaste={(e) => {
                  const text = (e.clipboardData?.getData("text") || "").trim();
                  if (!/^\d+$/.test(text)) e.preventDefault();
                }}
                style={{ width: 90 }}
              />
              <div style={{ fontSize: 12, marginTop: 4 }}>Minutes</div>
            </div>
          </Space>
        </div>
      </Modal>

      <Modal
        title={
          Number(selectedStatusValue) === 1
            ? "Reason for ETA"
            : Number(selectedStatusValue) === 2
              ? "Arrival Note"
              : "Repair Note"
        }
        open={reasonModalVisible}
        onOk={() => {
          setReasonModalVisible(false);
          setStatusModalVisible(true);
        }}
        onCancel={() => {
          setReasonModalVisible(false);
          revertStatusSelection();
          setStatusReason("");
        }}
        okText="Continue"
        cancelText="Cancel"
        okButtonProps={{
          disabled: !statusReason || statusReason.trim().length === 0,
        }}
        width={window.innerWidth < 768 ? "95%" : 520}
        destroyOnHidden
      >
        <div>
          <p style={{ marginBottom: 12 }}>
            {Number(selectedStatusValue) === 1 &&
              "Please provice a reason for ETA time."}
            {Number(selectedStatusValue) === 2 &&
              "Please provide an arrival note."}
            {Number(selectedStatusValue) === 4 &&
              "Please provide a diagnosis/repair note."}
          </p>
          <TextArea
            rows={4}
            maxLength={500}
            showCount
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            placeholder={
              Number(selectedStatusValue) === 1
                ? "e.g traffic, parts collection"
                : Number(selectedStatusValue) === 2
                  ? "e.g on site with driver, awaiting access"
                  : Number(selectedStatusValue) === 4
                    ? "e.g fault found, beginning replacement of tyre"
                    : ""
            }
            style={{ marginBottom: 8 }}
          />
        </div>
      </Modal>

      {/* Video Player Modal */}
      <Modal
        title={currentVideoTitle}
        open={videoModalVisible}
        onCancel={() => setVideoModalVisible(false)}
        footer={null}
        width="95%"
        centered
        destroyOnClose
        bodyStyle={{ padding: 0, backgroundColor: '#000' }}
      >
        <video
          src={currentVideoSrc}
          controls
          autoPlay
          playsInline
          preload="auto"
          style={{ width: '100%', maxHeight: '80vh', display: 'block' }}
          onError={(e) => {
            console.error('Video playback error:', e);
            message.error('Failed to play video. The video format may not be supported on this device.');
            setVideoModalVisible(false);
          }}
        />
      </Modal>

      {/* Custom Confirmation Modal */}
      <Modal
        title={
          confirmModalType === "accept"
            ? "Accept Breakdown"
            : "Decline Breakdown"
        }
        open={confirmModalVisible}
        onOk={handleModalConfirm}
        onCancel={handleModalCancel}
        okText={confirmModalType === "accept" ? "Yes, Accept" : "Yes, Decline"}
        cancelText="Cancel"
        okButtonProps={{
          type: confirmModalType === "accept" ? "primary" : "danger",
          loading: confirmModalLoading,
        }}
        cancelButtonProps={{ loading: confirmModalLoading }}
        maskClosable={false}
        closable={!confirmModalLoading}
      >
        <p>
          Are you sure you want to{" "}
          {confirmModalType === "accept" ? "accept" : "decline"} this breakdown?
          This action cannot be undone.
        </p>
      </Modal>

      {/* Assign Mechanic Confirmation (Supplier) */}
      <Modal
        title="Confirm Mechanic Assignment"
        open={assignConfirmVisible}
        onOk={handleAssignMechanic}
        onCancel={() => setAssignConfirmVisible(false)}
        okText="Assign"
        cancelText="Cancel"
        confirmLoading={assigningMechanic}
      >
        <p>
          Assign{" "}
          <strong>
            {selectedMechanic
              ? selectedMechanic.fullName ||
              selectedMechanic.mechanicName ||
              selectedMechanic.name
              : "selected mechanic"}
          </strong>
          {selectedMechanic?.companyName
            ? ` (${selectedMechanic.companyName})`
            : ""}{" "}
          to breakdown
          <strong>
            {" "}
            {breakdown?.breakdownReference || breakdown?.id || id}
          </strong>
          ?
        </p>
        <p>
          This will notify the selected mechanic and link them to this
          breakdown.
        </p>
      </Modal>

      {/* Complete Breakdown Modal */}
      <Modal
        title="Complete Breakdown"
        open={completeBreakdownModalVisible}
        onCancel={() => setCompleteBreakdownModalVisible(false)}
        footer={null}
        destroyOnClose
        width={window.innerWidth < 768 ? "95%" : 520}
        bodyStyle={{
          padding: "16px 24px",
          maxHeight: "calc(100vh - 200px)",
          overflowY: "auto",
        }}
        maskClosable={false}
      >
        <Form
          form={completeBreakdownForm}
          layout="vertical"
          onFinish={handleCompleteBreakdownSubmit}
          initialValues={{
            breakdownId: breakdown?.breakdownId,
          }}
          requiredMark="optional"
        >
          {/* Hidden BreakdownId */}
          <Form.Item
            name="breakdownId"
            initialValue={breakdown?.breakdownId}
            hidden={true}
          >
            <Input type="hidden" />
          </Form.Item>

          <Form.Item label="Search Category Combination">
            <Select
              allowClear
              filterOption={false}
              loading={completeCategorySearching}
              notFoundContent={completeCategorySearching ? <Spin size="small" /> : null}
              onChange={(value) => {
                if (!value) {
                  setCompleteCategorySearchValue(undefined);
                  return;
                }
                handleCompleteCategorySearchSelect(value);
              }}
              onClear={() => {
                setCompleteCategorySearchTerm("");
                setCompleteCategorySearchValue(undefined);
                setCompleteCategorySearchResults([]);
              }}
              onSearch={setCompleteCategorySearchTerm}
              options={completeCategorySearchResults.map((item) => ({
                label: buildCategorySearchLabel(item),
                value: buildCategorySearchValue(item),
              }))}
              placeholder="Search category, sub-category, or component"
              showSearch
              value={completeCategorySearchValue}
            />
          </Form.Item>

          {/* Breakdown Category */}
          <Form.Item
            name="breakdownCategory"
            label="Breakdown Category"
            rules={[
              { required: true, message: "Please select a breakdown category" },
            ]}
          >
            <Select
              placeholder="Select breakdown category"
              onChange={handleCategoryChange}
              loading={loadingCategories}
              showSearch
              optionFilterProp="label"
              options={breakdownCategories.map((category) => ({
                label: category.category,
                value: category.id,
              }))}
            />
          </Form.Item>

          {/* Breakdown Sub Category */}
          <Form.Item
            name="breakdownSubCategory"
            label="Breakdown Sub Category"
            rules={[
              {
                required: true,
                message: "Please select a breakdown sub-category",
              },
            ]}
          >
            <Select
              placeholder="Select breakdown sub-category"
              loading={loadingSubcategories}
              disabled={!selectedCategory}
              onChange={handleSubCategoryChange}
              showSearch
              optionFilterProp="label"
              options={breakdownSubcategories.map((subcategory) => ({
                label: subcategory.subCategory,
                value: subcategory.id,
              }))}
            />
          </Form.Item>

          {/* Breakdown Component */}
          <Form.Item
            name="breakdownComponent"
            label="Component"
            rules={[
              {
                required: true,
                message: "Please select a component",
              },
            ]}
          >
            <Select
              placeholder="Select component"
              loading={loadingComponents}
              disabled={!selectedSubCategory}
              onChange={handleComponentChange}
              showSearch
              optionFilterProp="label"
              options={breakdownComponents.map((component) => ({
                label: component.component,
                value: component.id,
              }))}
            />
          </Form.Item>

          {/* Cause (Optional) */}
          <Form.Item name="cause" label="Cause (Optional)">
            <Select
              placeholder="Select cause (optional)"
              allowClear
              disabled={!selectedComponent}
              onChange={handleCauseChange}
              loading={loadingCauses}
              showSearch
              optionFilterProp="label"
              options={breakdownCauses.map((cause) => ({
                label: cause.cause,
                value: cause.id,
              }))}
            />
          </Form.Item>

          {/* Tyre Position (conditional field only shown if TYRES category selected) */}
          {selectedCategory?.category?.toUpperCase() === "TYRES" && (
            <Form.Item
              label="Tyre Position"
              required
            >
              <Button block onClick={() => setTyrePickerVisible(true)}>
                {selectedTyres.length
                  ? `Tyre positions: ${selectedTyres.join(", ")}`
                  : "Select tyre positions"}
              </Button>
            </Form.Item>
          )}

          {/* Correction */}
          <Form.Item
            name="correction"
            label="Correction"
            rules={[
              { required: true, message: "Please provide correction details" },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="Enter details about how the breakdown was corrected..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          {/* Affected Asset */}
          <Form.Item
            name="affectedAsset"
            label="Affected Asset"
            rules={[
              { required: true, message: "Please select the affected asset" },
            ]}
          >
            <Select placeholder="Select affected asset">
              <Select.Option value="Truck">Horse</Select.Option>
              <Select.Option value="Trailer1">Trailer 1</Select.Option>
              <Select.Option value="Trailer2">Trailer 2</Select.Option>
            </Select>
          </Form.Item>

          {/* Form Actions */}
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => setCompleteBreakdownModalVisible(false)}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitLoading}
              icon={<CheckCircleOutlined />}
            >
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Select Tyre Position"
        open={tyrePickerVisible}
        onCancel={() => setTyrePickerVisible(false)}
        footer={
          <Space style={{ display: "flex", justifyContent: "end", width: "100%" }}>
            <Button onClick={() => setTyrePickerVisible(false)}>Cancel</Button>
            <Button
              disabled={!selectedTyres.length}
              onClick={() => setTyrePickerVisible(false)}
              type="primary"
            >
              Done
            </Button>
          </Space>
        }
        width={720}
        destroyOnHidden
      >
        <TyrePositionPicker
          selectedTyres={selectedTyres}
          onToggleTyre={toggleSelectedTyre}
        />
      </Modal>

      {/* Confirmation Modal for Complete Breakdown */}
      <Modal
        title="Confirm Complete Breakdown"
        open={confirmCompleteModalVisible}
        onOk={handleFinalSubmit}
        onCancel={() => setConfirmCompleteModalVisible(false)}
        okText="Complete"
        cancelText="Cancel"
        confirmLoading={submitLoading}
      >
        <p>Are you sure you want to complete this event?</p>
        <p>
          This action will mark the breakdown as resolved and cannot be undone.
        </p>
      </Modal>
      <RestrictionModal
        open={restriction}
        setOpen={setRestriction}
        item={!isShare ? "Call Driver button" : "Share button"}
      ></RestrictionModal>
    </div>
  );
};

export default BreakdownDetails;
